import { useMemo } from 'react';
import { useFormacao } from '@/context/FormacaoContext';
import { useFuncionariosFormacao } from '@/context/FuncionariosFormacaoContext';
import { estadoNormalizado } from '@/utils/estadoUtils';

function pct(a: number, b: number): number {
  return b === 0 ? 0 : Math.round((a / b) * 100);
}

function formatCurrency(v: number): string {
  return new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v);
}

interface Props {
  ano: number;
}

export default function FormacaoStatsPanel({ ano }: Props) {
  const { formacoes, participantes } = useFormacao();
  const { funcionarios } = useFuncionariosFormacao();

  // ── Filtro: formações do ano ─────────────────────────────────────────
  const fsAno = useMemo(() => {
    return formacoes.filter((f) => {
      if (!f.data_inicio) return false;
      return f.data_inicio.startsWith(`${ano}-`);
    });
  }, [formacoes, ano]);

  const idsFsAno = useMemo(() => new Set(fsAno.map((f) => f.id)), [fsAno]);
  const partsAno = useMemo(() => participantes.filter((p) => idsFsAno.has(p.formacao_id)), [participantes, idsFsAno]);

  // ── KPIs ─────────────────────────────────────────────────────────────
  const totalAcoes = fsAno.length;
  const concluidas = fsAno.filter((f) => estadoNormalizado(f.estado) === 'concluida').length;
  const emCurso = fsAno.filter((f) => estadoNormalizado(f.estado) === 'em_curso').length;
  const planeadas = fsAno.filter((f) => estadoNormalizado(f.estado) === 'planeada').length;
  const totalHoras = fsAno.reduce((s, f) => s + (f.duracao_horas ?? 0), 0);
  const totalCusto = fsAno.reduce((s, f) => s + (f.custo ?? 0), 0);
  const participantesUnicos = new Set(partsAno.map((p) => p.funcionario_id)).size;
  const certEmitidos = partsAno.filter((p) => p.certificado_emitido).length;
  const taxaCert = pct(certEmitidos, partsAno.length);
  const taxaCobertura = pct(participantesUnicos, funcionarios.length);

  // ── Por categoria ────────────────────────────────────────────────────
  const porCategoria = useMemo(() => {
    const map: Record<string, { acoes: number; horas: number; participantes: number }> = {};
    fsAno.forEach((f) => {
      const cat = f.categoria ?? 'Outros';
      if (!map[cat]) map[cat] = { acoes: 0, horas: 0, participantes: 0 };
      map[cat].acoes++;
      map[cat].horas += f.duracao_horas ?? 0;
      map[cat].participantes += participantes.filter((p) => p.formacao_id === f.id).length;
    });
    return Object.entries(map)
      .map(([cat, v]) => ({ cat, ...v }))
      .sort((a, b) => b.horas - a.horas);
  }, [fsAno, participantes]);

  const maxCatHoras = Math.max(...porCategoria.map((c) => c.horas), 1);

  // ── Por estado ───────────────────────────────────────────────────────
  const porEstado = useMemo(() => {
    return [
      { estado: 'concluida', label: 'Concluídas', color: '#10b981', bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500' },
      { estado: 'em_curso', label: 'Em Curso', color: '#f59e0b', bg: 'bg-amber-50', text: 'text-amber-700', dot: 'bg-amber-500' },
      { estado: 'planeada', label: 'Planeadas', color: '#71717a', bg: 'bg-zinc-100', text: 'text-zinc-600', dot: 'bg-zinc-400' },
      { estado: 'cancelada', label: 'Canceladas', color: '#ef4444', bg: 'bg-rose-50', text: 'text-rose-600', dot: 'bg-rose-400' },
    ].map((item) => {
      const count = fsAno.filter((f) => estadoNormalizado(f.estado) === item.estado).length;
      const pctVal = totalAcoes > 0 ? Math.round((count / totalAcoes) * 100) : 0;
      return { ...item, count, pct: pctVal };
    });
  }, [fsAno, totalAcoes]);

  // ── Top 5 formações ──────────────────────────────────────────────────
  const topFormacoes = useMemo(() => {
    return [...fsAno]
      .filter((f) => (f.total_participantes ?? 0) > 0)
      .sort((a, b) => (b.total_participantes ?? 0) - (a.total_participantes ?? 0))
      .slice(0, 5);
  }, [fsAno]);
  const maxPart = topFormacoes[0]?.total_participantes ?? 1;

  if (totalAcoes === 0) {
    return (
      <div className="bg-white rounded-xl border border-zinc-100 p-6">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-5 h-5 flex items-center justify-center">
            <i className="ri-bar-chart-grouped-line text-zinc-500 text-sm" />
          </div>
          <h3 className="text-[13px] font-bold text-zinc-800">Estatísticas de Formação — {ano}</h3>
        </div>
        <div className="flex flex-col items-center gap-2 py-6">
          <div className="w-10 h-10 flex items-center justify-center bg-zinc-100 rounded-full">
            <i className="ri-inbox-line text-zinc-400 text-lg" />
          </div>
          <p className="text-[12px] text-zinc-400">Sem dados para {ano}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-zinc-100 overflow-hidden">
      <div className="px-5 py-4 border-b border-zinc-50 flex items-center gap-2">
        <div className="w-5 h-5 flex items-center justify-center">
          <i className="ri-bar-chart-grouped-line text-zinc-500 text-sm" />
        </div>
        <h3 className="text-[13px] font-bold text-zinc-800">Estatísticas de Formação — {ano}</h3>
        <span className="text-[10px] font-semibold text-zinc-400 bg-zinc-100 px-2 py-0.5 rounded-full">{totalAcoes} ações</span>
      </div>

      <div className="p-5 space-y-6">
        {/* KPIs grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-zinc-100 flex-shrink-0">
              <i className="ri-book-open-line text-zinc-600 text-sm" />
            </div>
            <div>
              <p className="text-[18px] font-bold text-zinc-900 leading-tight">{totalAcoes}</p>
              <p className="text-[11px] text-zinc-500">Ações</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-amber-50 flex-shrink-0">
              <i className="ri-time-line text-amber-600 text-sm" />
            </div>
            <div>
              <p className="text-[18px] font-bold text-zinc-900 leading-tight">{totalHoras}h</p>
              <p className="text-[11px] text-zinc-500">Horas</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-emerald-50 flex-shrink-0">
              <i className="ri-user-star-line text-emerald-600 text-sm" />
            </div>
            <div>
              <p className="text-[18px] font-bold text-zinc-900 leading-tight">{participantesUnicos}</p>
              <p className="text-[11px] text-zinc-500">Formandos</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-zinc-100 flex-shrink-0">
              <i className="ri-funds-line text-zinc-600 text-sm" />
            </div>
            <div>
              <p className="text-[18px] font-bold text-zinc-900 leading-tight">{formatCurrency(totalCusto)}</p>
              <p className="text-[11px] text-zinc-500">Custo total</p>
            </div>
          </div>
        </div>

        {/* Detalhes secundários */}
        <div className="flex flex-wrap gap-4 text-[11px] text-zinc-500">
          <span><strong className="text-zinc-700">{concluidas}</strong> concluídas</span>
          <span><strong className="text-zinc-700">{emCurso}</strong> em curso</span>
          <span><strong className="text-zinc-700">{planeadas}</strong> planeadas</span>
          <span><strong className="text-zinc-700">{taxaCert}%</strong> certificados</span>
          <span><strong className="text-zinc-700">{taxaCobertura}%</strong> cobertura</span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {/* Por categoria */}
          <div>
            <h4 className="text-[11px] font-bold text-zinc-700 mb-3">Horas por Categoria</h4>
            <div className="space-y-2">
              {porCategoria.map((item) => (
                <div key={item.cat} className="flex items-center gap-3">
                  <p className="text-[11px] font-medium text-zinc-600 w-32 flex-shrink-0 truncate">{item.cat}</p>
                  <div className="flex-1 h-2 rounded-full bg-zinc-100 overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all"
                      style={{ width: `${Math.round((item.horas / maxCatHoras) * 100)}%`, backgroundColor: '#18181b' }}
                    />
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="text-[12px] font-bold text-zinc-800 tabular-nums w-10">{item.horas}h</span>
                    <span className="text-[10px] text-zinc-400 w-12">{item.participantes} p.</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Por estado */}
          <div>
            <h4 className="text-[11px] font-bold text-zinc-700 mb-3">Ações por Estado</h4>
            <div className="space-y-2">
              {porEstado.map((item) => (
                <div key={item.estado} className="flex items-center gap-3">
                  <div className={`w-7 h-7 flex items-center justify-center rounded-lg flex-shrink-0 ${item.bg}`}>
                    <span className={`text-[13px] font-bold ${item.text}`}>{item.count}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[11px] font-medium text-zinc-600">{item.label}</span>
                      <span className="text-[11px] text-zinc-400">{item.pct}%</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-zinc-100 overflow-hidden">
                      <div className="h-full rounded-full transition-all" style={{ width: `${item.pct}%`, backgroundColor: item.color }} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Top 5 */}
        {topFormacoes.length > 0 && (
          <div>
            <h4 className="text-[11px] font-bold text-zinc-700 mb-3">Top 5 — Mais Frequentadas ({ano})</h4>
            <div className="space-y-2">
              {topFormacoes.map((f, i) => (
                <div key={f.id} className="flex items-center gap-3">
                  <span className="text-[11px] font-bold text-zinc-300 w-4 text-center flex-shrink-0">{i + 1}</span>
                  <div className="flex-1 min-w-0 space-y-1">
                    <p className="text-[12px] font-semibold text-zinc-800 truncate">{f.nome}</p>
                    <div className="h-1.5 rounded-full bg-zinc-100 overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all"
                        style={{ width: `${Math.round(((f.total_participantes ?? 0) / maxPart) * 100)}%`, backgroundColor: '#18181b' }}
                      />
                    </div>
                  </div>
                  <div className="flex-shrink-0 text-right">
                    <p className="text-[13px] font-bold text-zinc-800 tabular-nums">{f.total_participantes}</p>
                    <p className="text-[10px] text-zinc-400">part.</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}